import { DocumentsCard } from '@/components/Timeline/Documents';
import { TabOption, TimelineTabs } from '@/components/Timeline/TimelineTabs';
import { partitionClinicalTasks } from '@bariendo/lib/clinical-tasks';
import { Box } from '@mantine/core';
import { IconListCheck, IconFileText, IconListDetails } from '@tabler/icons-react';
import { ReactElement, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { TIMELINE_INFO_TAB_QUERY_PARAM } from '@bariendo/lib/constants';
import { useTimelineContext } from './hooks/useTimelineContext';
import { PatientSummary } from './PatientSummary';
import { TasksManagement } from './TasksManagement/TasksManagement';
import { useClinicalTasks } from '@/hooks/useClinicalTasks';
import { useFeatureFlagEnabled } from '@/hooks/useFeatureFlag';
import { FEATURE_FLAGS } from '@bariendo/lib/constants';

enum TabType {
  DETAILS = 'details',
  TASKS = 'tasks',
  DOCUMENTS = 'documents',
}

// Warning-amber from the B2B track pill (TrackPill.tsx) — "needs attention",
// distinct from Error-Red which is reserved for overdue.
const OPEN_TASKS_DOT_COLOR = '#D89614';
const OVERDUE_TASKS_DOT_COLOR = 'var(--mantine-color-error)';

const TABS: TabOption<TabType>[] = [
  { label: 'General', icon: <IconListDetails size={14} />, value: TabType.DETAILS },
  { label: 'Tasks', icon: <IconListCheck size={14} />, value: TabType.TASKS },
  { label: 'Documents', icon: <IconFileText size={14} />, value: TabType.DOCUMENTS },
];

function TasksDot({ color }: { color: string }): ReactElement {
  return (
    <span
      aria-label="This patient has outstanding tasks"
      style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: color, display: 'inline-block' }}
    />
  );
}

export function PatientInfoPanel(): ReactElement {
  const { patientContext } = useTimelineContext();
  const patient = patientContext?.patient;
  const [activeTab, setActiveTab] = useState<TabType>(TabType.DETAILS);

  // Deep link from the inbox / Task log: ?infoTab=tasks selects the Tasks tab.
  // Applied in an effect rather than as initial state because this panel does
  // NOT remount when navigating patient→patient (same route), which is
  // exactly how the Task log navigates — a lazy initializer would run once
  // and every later deep link would silently do nothing. Keyed on the patient
  // too, so row→row navigation re-applies it.
  const [searchParams] = useSearchParams();
  const infoTabParam = searchParams.get(TIMELINE_INFO_TAB_QUERY_PARAM);
  useEffect(() => {
    if (infoTabParam === TabType.TASKS) {
      setActiveTab(TabType.TASKS);
    }
  }, [infoTabParam, patient?.id]);
  const isTasksEnabled = useFeatureFlagEnabled(FEATURE_FLAGS.ENABLE_TASK_MANAGEMENT);

  // Lifted out of the Tasks tab so the dot reflects open tasks without the
  // tab ever having been opened (and the list is instant when it is).
  const { tasks, loading, refresh } = useClinicalTasks(isTasksEnabled ? patient : undefined);

  const tabs = useMemo(() => {
    const { open, overdue } = partitionClinicalTasks(tasks);
    const dotColor = overdue.length > 0 ? OVERDUE_TASKS_DOT_COLOR : open.length > 0 ? OPEN_TASKS_DOT_COLOR : undefined;

    return TABS.filter((tab) => {
      if (tab.value === TabType.TASKS) {
        return isTasksEnabled;
      }
      return true;
    }).map((tab) =>
      tab.value === TabType.TASKS && dotColor ? { ...tab, indicator: <TasksDot color={dotColor} /> } : tab
    );
  }, [isTasksEnabled, tasks]);

  return (
    <>
      <Box
        top={0}
        mb={16}
        pos="sticky"
        bg="var(--mantine-color-body, #fff)"
        style={{
          zIndex: 20,
          marginInline: 'calc(-1 * var(--mantine-spacing-md))',
          paddingInline: 'var(--mantine-spacing-md)',
          marginTop: 'calc(-1 * var(--mantine-spacing-md))',
          paddingTop: 'var(--mantine-spacing-md)',
          borderBottom: '1px solid var(--mantine-color-gray-2)',
        }}
      >
        <TimelineTabs tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} />
      </Box>

      {activeTab === TabType.DETAILS && <PatientSummary />}
      {activeTab === TabType.TASKS && isTasksEnabled && (
        <TasksManagement tasks={tasks} loading={loading} refresh={refresh} />
      )}
      {activeTab === TabType.DOCUMENTS && <DocumentsCard resource={patient} />}
    </>
  );
}
